/**
 * What the question dock needs to decide, kept pure so it is tested: which
 * questions are waiting, which key picks what, what a multiple pick sends,
 * and what the answers become.
 */

import type { AnswerItem, Ask, Message } from '../api';

export interface PendingAsk {
  /** The reply that asked, which the answer names as `answers`. */
  messageId: string;
  ask: Ask;
}

/** The questions waiting on the person: the chat's last message, when it is
 *  a reply that asked some nobody has answered. */
export function pendingAsk(messages: readonly Message[]): PendingAsk | null {
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'assistant' || last.body.kind !== 'text') return null;
  const { ask } = last.body;
  return ask && ask.answer === null && ask.questions.length ? { messageId: last.id, ask } : null;
}

/** The option a number key picks, counting from 1; null for any other key. */
export function optionForKey(key: string, count: number): number | null {
  if (!/^[1-9]$/.test(key)) return null;
  const n = Number(key) - 1;
  return n < count ? n : null;
}

/** A multiple pick as one answer: the picked options in the order offered. */
export const joinPicks = (options: readonly string[], picked: ReadonlySet<number>): string =>
  options.filter((_, i) => picked.has(i)).join(', ');

/** The dock's answers beside the questions they answer. */
export const answerItems = (ask: Ask, picks: readonly string[]): AnswerItem[] =>
  ask.questions.map((q, i) => ({ question: q.question, answer: picks[i] ?? '' }));

/** The answers as the message text, as the Worker writes it
 *  (`answeredText`, worker/lib/engine/turn.ts). */
export const answeredText = (items: readonly AnswerItem[]): string =>
  items.length === 1 ? items[0]!.answer : items.map((i) => `${i.question}\n${i.answer}`).join('\n\n');
